import { toast } from "react-toastify";
import { supabase } from "../../../context/useSupabaseContext";
import { userDataType } from "../../../stores/accountManagementStore";

interface UpdateUserInfoParams {
    email?: string;
    password?: string;
    display_name?: string;
    avatar_url?: string;
}

export default async function updateUserInfo(params: UpdateUserInfoParams): Promise<userDataType | null> {
    const { email, password, display_name, avatar_url } = params;

    const updatePromise = async () => {
        const updateData: {
            email?: string;
            password?: string;
            data?: {
                display_name?: string;
                avatar_url?: string;
            };
        } = {};

        // Email and password go at the top level
        if (email) updateData.email = email;
        if (password) updateData.password = password;

        // display_name and avatar_url go in the data object
        if (display_name || avatar_url) {
            updateData.data = {
                ...(display_name && { display_name }),
                ...(avatar_url && { avatar_url }),
            };
        }

        const { data, error } = await supabase.auth.updateUser(updateData);

        if (error) {
            if (error.message === "User already registered") {
                throw new Error("An account with this email already exists");
            }
            if (error.message.includes("Password should be at least")) {
                throw new Error(error.message);
            }
            if (error.message === "Invalid login credentials") {
                throw new Error("Invalid email or password");
            }
            if (error.message.includes("A resource with this ID already exists")) {
                throw new Error(error.message);
            }
            if (error.message === "A resource with this ID") {
                throw new Error(error.message);
            }
            throw new Error(error.message);
        }

        if (!data.user) {
            throw new Error("Failed to update user info");
        }

        // Get the current session to access access_token and refresh_token
        const { data: sessionData } = await supabase.auth.getSession();

        const userData: userDataType = {
            id: data.user.id,
            email: data.user.email || email || "",
            profilePicURL: data.user.user_metadata?.avatar_url as string | undefined,
            userName: data.user.user_metadata?.display_name as string | undefined,
            providers: data.user.app_metadata?.providers || []
        };

        return userData;
    };

    try {
        const result = await toast.promise(
            updatePromise(),
            {
                pending: "Updating profile...",
                success: "Profile updated successfully!",
                error: "Failed to update profile"
            }
        );
        return result;
    } catch (err) {
        console.log("Update user info error:", err);
        return null;
    }
}
